import { v4 as uuid } from "uuid";
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function generateId() {
  return uuid();
}

export function generateThreadTitle(userInput: string): string {
  const titleMaxLength = 200; // As per palantir API requirements
  return userInput.length > titleMaxLength ? userInput.slice(0, titleMaxLength) + "..." : userInput;
}

export function formatDate(dateValue?: string | Date) {
  if (!dateValue) return "(no date)";
  try {
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return typeof dateValue === "string" ? dateValue : "(no date)";
  }
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
}

/**
 * Display-only USD formatter. Accepts the raw string/number values we get
 * from the ontology and never mutates them, just turns them into something
 * like "$1,234.56". Falls back to a muted em-dash for nullish/non-numeric
 * input. **Do not** use the output as a value — it includes "$" and commas
 * that downstream XML / API calls can't parse.
 */
export function formatUSD(value: string | number | null | undefined, fallback = "—"): string {
  if (value === null || value === undefined || value === "") return fallback;
  const n = typeof value === "number" ? value : parseFloat(String(value));
  if (!Number.isFinite(n)) return fallback;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B";

  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

/**
 * Helper function to safely check if a feature is enabled in organization settings
 * @param settings - The organization settings object (can be null/undefined)
 * @param key - The feature key to check
 * @param defaultWhenUnset - Value to return when the key is absent from settings.
 *   Defaults to `false` (opt-in features). Pass `true` for kill-switch flags that
 *   should be on unless an admin explicitly disables them (e.g. `enableGeneralAnalysis`).
 * @returns true if the feature should be treated as enabled
 */
export function isFeatureEnabled(settings: unknown, key: string, defaultWhenUnset = false): boolean {
  // Check if settings exists and is a valid object
  if (!settings || typeof settings !== "object") {
    return defaultWhenUnset;
  }

  // Fall back to the default when the org hasn't set this key yet.
  if (!(key in settings)) {
    return defaultWhenUnset;
  }

  // Once present, only a strict boolean true counts as enabled.
  const value = (settings as Record<string, unknown>)[key];
  return value === true;
}

export function hasChartableData(content: string): boolean {
  if (/^\s*\|.+\|\s*$/m.test(content)) return true;
  const numbers = content.match(/(?:\$\s*)?\d[\d,]*(?:\.\d+)?%?/g) ?? [];
  return numbers.length >= 3;
}

export function getNameInitials(name: string = ""): string {
  return name
    .split(" ")
    .map(n => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

/**
 * Returns the rule name when the invoice was auto-approved by a smart rule, else null.
 */
export function getAutoApprovedByRule(metadata: unknown): { id: string | null; name: string } | null {
  if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) {
    return null;
  }
  const value = (metadata as Record<string, unknown>).autoApprovedByRule;

  if (value && typeof value === "object" && !Array.isArray(value)) {
    const rule = value as Record<string, unknown>;
    const name = typeof rule.name === "string" ? rule.name.trim() : "";
    if (!name) return null;
    return { id: typeof rule.id === "string" && rule.id ? rule.id : null, name };
  }

  if (typeof value === "string" && value.trim()) {
    return { id: null, name: value.trim() };
  }

  return null;
}
